import {
  IntranetOccasionKind,
  IntranetWidgetKind,
  intranetContentPath,
  type IntranetContentSummary,
  type IntranetOccasion,
  type IntranetWidget,
} from '@gb-ticket/shared-types';
import { CakeIcon, Loader2Icon } from 'lucide-react';
import { Link } from 'react-router';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { formatDateTime } from '@/lib/format';
import { useMe } from '@/platform/auth/use-me';
import { useVoiceCopy } from '@/platform/voice/use-voice';
import { ProductTile } from '@/platform/components/product-tile';
import { intranetHomeModuleTiles } from '@/products/intranet/features/intranet/product-tiles';
import { usePendingIntranetAcknowledgements } from '@/products/intranet/features/intranet/use-intranet-ack';
import { useIntranetBirthdays } from '@/products/intranet/features/intranet/use-intranet-directory';

const MONTHS = [
  '',
  'Januar',
  'Februar',
  'März',
  'April',
  'Mai',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'Dezember',
];

/** Known gadgets only. An unknown kind renders nothing and does not break the column. */
export function IntranetGadgets({ widgets }: { widgets: readonly IntranetWidget[] }) {
  return (
    <div className="min-w-0 space-y-4">
      {widgets.map((widget) => (
        <Gadget key={widget.id} kind={widget.kind} />
      ))}
    </div>
  );
}

function Gadget({ kind }: { kind: string }) {
  if (kind === IntranetWidgetKind.PENDING_ACK) {
    return <PendingAckGadget />;
  }
  if (kind === IntranetWidgetKind.BIRTHDAYS) {
    return <BirthdayGadget />;
  }
  if (kind === IntranetWidgetKind.MODULES) {
    return <ModulesGadget />;
  }
  return null;
}

function PendingAckGadget() {
  const pending = usePendingIntranetAcknowledgements();
  const items = pending.data?.items ?? [];
  if (pending.isPending || items.length === 0) {
    return pending.isPending ? <GadgetLoading label="Ankündigungen werden geladen …" /> : null;
  }
  return (
    <section className="space-y-2" aria-label="Wichtige Ankündigungen">
      <h3 className="text-sm font-semibold">Wichtige Ankündigungen</h3>
      <ul className="space-y-2">
        {items.map((item) => (
          <li key={item.id}>
            <PendingAckBanner item={item} />
          </li>
        ))}
      </ul>
    </section>
  );
}

function PendingAckBanner({ item }: { item: IntranetContentSummary }) {
  const copy = useVoiceCopy();
  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-amber-950">
      <p className="text-sm font-medium">
        <Link to={intranetContentPath(item)} className="underline-offset-2 hover:underline">
          {item.title}
        </Link>
      </p>
      <p className="mt-1 text-sm">
        {copy('intranet.ack.heading')}
        {item.ackDeadline ? ` (Frist: ${formatDateTime(item.ackDeadline)})` : ''}.
      </p>
    </div>
  );
}

function BirthdayGadget() {
  const birthdays = useIntranetBirthdays();
  const items = birthdays.data?.items ?? [];
  if (birthdays.isPending || items.length === 0) {
    return birthdays.isPending ? <GadgetLoading label="Termine werden geladen …" /> : null;
  }
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <CakeIcon className="size-4" aria-hidden />
          Geburtstage und Jubiläen
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="space-y-2 text-sm">
          {items.map((item) => (
            <li key={`${item.kind}-${item.userId}-${item.month}-${item.day}`}>
              <BirthdayLine item={item} />
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

function BirthdayLine({ item }: { item: IntranetOccasion }) {
  const when = item.kind === IntranetOccasionKind.BIRTHDAY ? 'Geburtstag' : 'Jubiläum';
  const years =
    item.kind === IntranetOccasionKind.ANNIVERSARY && item.years ? ` (${item.years} Jahre)` : '';
  const soon = item.daysUntil === 0 ? ' · heute' : item.daysUntil === 1 ? ' · morgen' : '';
  return (
    <>
      <span className="font-medium">{item.displayName}</span>
      <span className="text-muted-foreground">
        {' '}
        · {when} am {item.day}. {MONTHS[item.month]}
        {years}
        {soon}
      </span>
    </>
  );
}

function ModulesGadget() {
  const me = useMe();
  const tiles = me.data ? intranetHomeModuleTiles(me.data) : [];
  if (tiles.length === 0) {
    return null;
  }
  return (
    <section
      aria-label="Anwendungen"
      className="min-w-0 space-y-3 rounded-xl border border-border bg-card p-4 shadow-sm"
    >
      <h3 className="text-sm font-semibold text-primary">Anwendungen</h3>
      <div className="flex min-w-0 flex-col gap-2">
        {tiles.map((tile) => (
          <ProductTile key={tile.code} tile={tile} compact />
        ))}
      </div>
    </section>
  );
}

function GadgetLoading({ label }: { label: string }) {
  return (
    <p className="flex items-center gap-2 text-sm text-muted-foreground">
      <Loader2Icon className="size-4 animate-spin" aria-hidden />
      {label}
    </p>
  );
}
